import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Edit, User, Paperclip, FileText, X, Loader2 } from 'lucide-react';

export const EditDraftDialog = ({
  draft, onClose, subject, setSubject, body, setBody,
  attachments, setAttachments, libraryDocs, loadingDocs, onSave, onGoLibrary,
}) => (
  <Dialog open={!!draft} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" /> Edit Email Draft</DialogTitle>
      </DialogHeader>
      {draft && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-lg">
            <User className="h-3.5 w-3.5" /><span>To:</span>
            <span className="font-medium text-foreground">{draft.lead?.name}</span>
            <span className="text-xs">({draft.lead?.email})</span>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="edit-subject-input" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Body</label>
            <textarea className="w-full min-h-[240px] p-3 border border-input rounded-lg text-sm resize-y bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              value={body} onChange={(e) => setBody(e.target.value)} data-testid="edit-body-input" />
          </div>
          <div data-testid="attachment-picker-section">
            <label className="text-sm font-medium mb-2 flex items-center gap-1.5">
              <Paperclip className="h-3.5 w-3.5" /> Attachments
              {attachments.length > 0 && <Badge variant="secondary" className="text-[10px] ml-1">{attachments.length}</Badge>}
            </label>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {attachments.map((att, i) => (
                  <Badge key={att.id || i} variant="outline" className="text-xs gap-1 py-1 px-2 bg-blue-500/5">
                    <FileText className="h-3 w-3" />{att.filename || att.name}
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="ml-0.5 text-muted-foreground hover:text-red-500"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="border rounded-lg max-h-32 overflow-y-auto">
              {loadingDocs ? (
                <p className="text-xs text-center py-3 text-muted-foreground"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Loading library…</p>
              ) : libraryDocs.length === 0 ? (
                <p className="text-xs text-center py-3 text-muted-foreground">No documents. <button onClick={onGoLibrary} className="text-primary underline">Upload files</button></p>
              ) : (
                libraryDocs.filter(d => !attachments.some(a => (a.id || a.doc_id) === d.id)).map(doc => (
                  <button key={doc.id} onClick={() => setAttachments(prev => [...prev, { id: doc.id, filename: doc.filename, content_type: doc.content_type, size: doc.size }])}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 text-xs" data-testid={`attach-doc-${doc.id}`}>
                    <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{doc.filename}</span>
                    <span className="text-muted-foreground shrink-0">{doc.size ? (doc.size / 1024).toFixed(0) + 'KB' : ''}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} data-testid="save-draft-btn">Save Changes</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
